import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Label } from "../ui/label";
import { ExternalLink, Save } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import { motion } from "framer-motion";
import RichTextEditor from "../ui/RichTextEditor";
import PageHeader from "../admin/PageHeader";
import CertificateFileUploader from "../ui/CertificateFileUploader";
import DatePicker from "../ui/DatePicker";
import IndustrySelect from "../ui/IndustrySelect";
import { createCertification } from "@/hooks/useCertification";

const AddCertification = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    industry: "",
    company: "",
    issueDate: "",
    verifyUrl: "",
    file: null,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.file) {
      toast({
        title: "File required",
        description: "Please upload a certificate file (PNG, JPG, or PDF).",
        variant: "destructive",
      });
      return;
    }

    if (!formData.industry?.trim()) {
      toast({
        title: "Industry required",
        description: "Please select or enter an industry / field.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const fd = new FormData();
      fd.append("title", formData.title);
      fd.append("description", formData.description);
      fd.append("industry", formData.industry.trim());
      fd.append("issueDate", formData.issueDate);
      if (formData.company?.trim()) {
        fd.append("company", formData.company.trim());
      }
      if (formData.verifyUrl?.trim()) {
        fd.append("verifyUrl", formData.verifyUrl.trim());
      }
      fd.append("file", formData.file);

      await createCertification(fd);

      toast({
        title: "Certification Added!",
        description: "Your certification has been successfully created.",
      });

      navigate("/admin/certifications");
    } catch (error) {
      toast({
        title: "Error",
        description:
          error.response?.data?.message ||
          "Failed to add certification. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      <PageHeader
        title="Add Certification"
        description="Add a new professional certification"
        routeTo="/admin/certifications"
        backTo="Certifications"
      />

      <form onSubmit={handleSubmit}>
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Certification Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="e.g. AWS Solutions Architect Associate"
                    required
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="industry">Industry / Field *</Label>
                    <IndustrySelect
                      id="industry"
                      value={formData.industry}
                      onChange={(industry) =>
                        setFormData((prev) => ({ ...prev, industry }))
                      }
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company">Company / Issuer (Optional)</Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="e.g. AWS, Coursera, Karachi AI"
                    />
                    <p className="text-xs text-muted-foreground">
                      Where you earned this certification
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="issueDate">Issue Date *</Label>
                  <DatePicker
                    id="issueDate"
                    value={formData.issueDate}
                    onChange={(issueDate) =>
                      setFormData((prev) => ({ ...prev, issueDate }))
                    }
                    placeholder="Select issue date"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="verifyUrl">Verify URL (Optional)</Label>
                  <div className="relative">
                    <ExternalLink className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                    <Input
                      id="verifyUrl"
                      name="verifyUrl"
                      value={formData.verifyUrl}
                      onChange={handleChange}
                      placeholder="https://www.credly.com/badges/..."
                      className="pl-10"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    If set, a Verify button will appear on the public page.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Description *</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Describe the certification using the rich text editor
                </p>
              </CardHeader>
              <CardContent>
                <RichTextEditor
                  value={formData.description}
                  onChange={(description) =>
                    setFormData((prev) => ({ ...prev, description }))
                  }
                  placeholder="Write about this certification..."
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Certificate File *</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Upload PNG, JPG, or PDF
                </p>
              </CardHeader>
              <div className="pl-6 pb-6">
                <CertificateFileUploader
                  value={formData.file}
                  onChange={(file) =>
                    setFormData((prev) => ({ ...prev, file }))
                  }
                />
              </div>
            </Card>
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  type="submit"
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                      className="w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"
                    />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  {isSubmitting ? "Saving..." : "Save Certification"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => navigate("/admin/certifications")}
                >
                  Cancel
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </motion.div>
  );
};

export default AddCertification;
